<template>
  <kFieldWrapper>
    <div class="k-form-field-wrap">
      <KDatePicker
        :valid="props.valid"
        :id="props.id"
        :value="props.value instanceof Date ? props.value : undefined"
        :disabled="props.disabled"
        :placeholder="placeHolderText"
        :label="labelText"
        :min="new Date(1900, 0, 1)"
        :max="new Date()"
        :start="'decade'"
        :depth="'month'"
        :size="'medium'"
        :rounded="'medium'"
        :focusedDate="parsedValue || new Date(1990, 0, 1)"
        @change="changeHandler($event)"
        @blur="blurHandler($event)"
        @focus="focusHandler($event)"
      />
      <kError
        :id="errorId"
        v-if="showValidationMessage"
      >
        {{ validationMessage }}
      </kError>
      <kHint
        :id="hintId"
        v-else
        >{{ hint }}</kHint
      >
    </div>
  </kFieldWrapper>
</template>

<script setup lang="ts">
import { FieldWrapper as kFieldWrapper } from "@progress/kendo-vue-form";
import { DatePicker as KDatePicker } from '@progress/kendo-vue-dateinputs'
import {
  Error as kError,
  Hint as kHint,
} from "@progress/kendo-vue-labels";
import { computed, ref } from "vue";
import { useLogger } from "vue-logger-plugin";

const logger = useLogger();

const props = defineProps({
  optional: Boolean,
  disabled: Boolean,
  placeholder: String,
  addAsterisk: Boolean,
  touched: Boolean,
  label: String,
  validationMessage: String,
  hint: String,
  id: String,
  valid: Boolean,
  value: {
    type: [Date, String],
    default: null,
  }
});

const emit = defineEmits(["change", "blur", "focus"]);
const parsedValue = props.value
    ? new Date(props.value)
    : null;

const showValidationMessage = computed(
  () => props.touched && props.validationMessage,
);
const showHint = computed(() => !showValidationMessage.value && props.hint);
const hintId = computed(() => (showHint.value ? `${props.id}_hint` : ""));
const errorId = computed(() =>
  showValidationMessage.value ? `${props.id}_error` : "",
);

const isFocused = ref(false);

const placeHolderText = ref(props.placeholder);
const labelText = ref(props.label);

if (props.addAsterisk) {
  placeHolderText.value = props.placeholder + " *";
  labelText.value = props.label + " *";
}

if (props.placeholder === undefined) {
  placeHolderText.value = "";
}

function changeHandler(event: unknown) {
  logger.debug("ApplicationFieldDate", "changeHandler", "event:", event);
  const value = (event as { value?: unknown }).value ? new Date((event as { value: unknown }).value as ConstructorParameters<typeof Date>[0]) : null;
  logger.debug("ApplicationFieldDate", "changeHandler", "emitting value:", value);
  emit("change", { value });
  isFocused.value = true;
}

function blurHandler(e: unknown) {
  logger.debug("ApplicationFieldDate", "blurHandler", "e:", e);
  emit("blur", e);
  isFocused.value = false;
}

function focusHandler(e: unknown) {
  logger.debug("ApplicationFieldDate", "focusHandler", "e:", e);
  emit("focus", e);
  isFocused.value = true;
}

</script>
